import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useMemo } from 'react'

import type { PieChartSliceData } from '@/pages/ui/pie-chart/types'

import { SELECT_FILTER_OPTION_VALUE_MAP, type SelectFilterOptionValue } from '@/components/select-filter/constants'

import { getCaseTypeBreakdownQuery } from '@/lib/queries/business/analysis/queries'
import { CASE_ARCHIVE_STATUS_MAP } from '@/lib/queries/business/constants'

export const useCaseTypeBreakdown = () => {
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([])
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [selectedActiveInactive, setSelectedActiveInactive] = useState<string[]>(['active'])
  const [selectedTagCondition, setSelectedTagCondition] = useState<SelectFilterOptionValue>(
    SELECT_FILTER_OPTION_VALUE_MAP.or
  )

  const archiveStatus = useMemo(() => {
    const len = selectedActiveInactive.length
    if (len === 2 || len === 0) return CASE_ARCHIVE_STATUS_MAP.all
    if (selectedActiveInactive.includes('active')) return CASE_ARCHIVE_STATUS_MAP.active
    return CASE_ARCHIVE_STATUS_MAP.archived
  }, [selectedActiveInactive])

  const { data: dataCaseType, isFetching: isFetchingCaseType } = useQuery(
    getCaseTypeBreakdownQuery({
      assignees: selectedAssignees,
      tags: selectedTags,
      tagCondition: selectedTagCondition,
      archiveStatus,
    })
  )

  const chartData = useMemo(() => {
    const data = (dataCaseType?.results ?? []).map((x) => {
      if (x.key.toLowerCase() === 'eoir') {
        return { name: 'EOIR', value: x.count, ratio: x.ratio }
      }
      return { name: x.key, value: x.count, ratio: x.ratio }
    })
    return data.sort((a, b) => b.value - a.value)
  }, [dataCaseType])

  const [chartDataNormal, chartDataSmall] = useMemo(() => {
    return [chartData.filter((x) => x.ratio >= 0.01), chartData.filter((x) => x.ratio < 0.01)]
  }, [chartData])

  const chartDataNormalized = useMemo(() => {
    const [othersCount, othersRatio] = chartDataSmall.reduce((acc, x) => [acc[0] + x.value, acc[1] + x.ratio], [0, 0])
    const others = { others: true, name: 'Others', value: othersCount, ratio: othersRatio }
    const items = [...chartDataNormal]
    if (chartDataSmall.length) {
      items.push(others)
    }
    return items
  }, [chartDataNormal, chartDataSmall])

  const handleReset = () => {
    setSelectedAssignees([])
    setSelectedTags([])
    setSelectedActiveInactive([])
  }

  const handleCaseTypeClick = (x: PieChartSliceData) => {
    if (x.name.toLowerCase() === 'eoir') {
      window.open(`/cases-eoir/active`, '_blank')
    } else {
      window.open(`/cases/active?caseTypes=${x.name}`, '_blank')
    }
  }

  return {
    selectedAssignees,
    setSelectedAssignees,
    selectedTags,
    setSelectedTags,
    selectedActiveInactive,
    setSelectedActiveInactive,
    selectedTagCondition,
    setSelectedTagCondition,
    isFetchingCaseType,
    chartDataNormalized,
    chartDataNormal,
    chartDataSmall,
    onFilterReset: handleReset,
    onCaseTypeClick: handleCaseTypeClick,
  }
}
